import { Order } from "../OrderList/types";

export const getOrderStatus = (order: Order): string => {
  return (
    order?.store_order_status?.status ||
    order?.status ||
    "NEW"
  ).toUpperCase();
};

export const isNewOrder = (order: Order): boolean => {
  const status = getOrderStatus(order);
  return status === "NEW" || status === "PENDING";
};


// Check if order belongs to the "Packing" tab (active fulfillment stage)
export const isPackingTabOrder = (order: Order): boolean => {
  const status = getOrderStatus(order);
  return (
    status === "PROCESSING" ||
    status === "PARTIALLY_PROCESSING" ||
    status === "PACKING" ||
    status === "READY_TO_PICK" ||
    status === "READY_TO_SHIP" ||
    status === "READY TO PICK" ||
    status === "READY TO SHIP"
  );
};

export const isPackingOrder = isPackingTabOrder;

// Can this order be marked as processing?
export const canMarkAsProcessing = (order: Order): boolean => {
  const status = getOrderStatus(order);
  const canChange = order?.store_order_status?.can_change !== false;
  return (status === "NEW" || status === "PENDING") && canChange;
};

// Can this order be marked ready? (Must be in processing/packing, not yet ready)
export const canMarkAsReady = (order: Order): boolean => {
  const status = getOrderStatus(order);
  const canChange = order?.store_order_status?.can_change !== false;
  return (
    (status === "PROCESSING" ||
      status === "PARTIALLY_PROCESSING" ||
      status === "PACKING") &&
    canChange
  );
};

// Has this order already been marked ready?
export const isReadyOrder = (order: Order): boolean => {
  const status = getOrderStatus(order);
  return (
    status === "READY_TO_SHIP" ||
    status === "READY TO SHIP" ||
    status === "READY_TO_PICK" ||
    status === "READY TO PICK"
  );
};

export const isInTransitOrder = (order: Order): boolean => {
  const status = getOrderStatus(order);
  return (
    status === "IN_TRANSIT" ||
    status === "OUT_FOR_DELIVERY" ||
    status === "OUT FOR DELIVERY" ||
    status === "SHIPPED"
  );
};

export const isOthersOrder = (order: Order): boolean => {
  return (
    !isNewOrder(order) &&
    !isPackingTabOrder(order) &&
    !isInTransitOrder(order)
  );
};

// Can this order be updated via the modal? (Only when not terminal and backend allows change)
export const canUpdateOrder = (order: Order): boolean => {
  const status = getOrderStatus(order);
  const canChange = order?.store_order_status?.can_change !== false;
  const isTerminal =
    status === "DELIVERED" ||
    status === "COMPLETE" ||
    status === "CANCELLED" ||
    status === "REFUNDED" ||
    status === "RETURN" ||
    status.includes("CANCEL") ||
    status.includes("DELIVERED");

  return canChange && !isTerminal;
};

export const getDisplayOrderStatus = (order: Order): string => {
  return order?.store_order_status?.status || order?.status || "New";
};

export const getCustomerName = (order: Order): string => {
  const firstName = order?.shipping_data?.first_name?.trim() || "";
  const lastName = order?.shipping_data?.last_name?.trim() || "";
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;
  if (order?.shipping_data?.Email_Address) return order.shipping_data.Email_Address;
  if (order?.email_client) return order.email_client;
  return `Customer #${order?.id || ""}`.trim();
};

export const getOrderStatusBadgeStyle = (
  statusStr: string
): { backgroundColor: string; color: string } => {
  const s = (statusStr || "").toUpperCase();
  if (s === "NEW" || s === "PENDING") {
    return { backgroundColor: "#DC2626", color: "#FFFFFF" }; // Red
  }
  if (
    s === "PROCESSING" ||
    s === "PARTIALLY_PROCESSING" ||
    s === "PACKING"
  ) {
    return { backgroundColor: "#D97706", color: "#FFFFFF" }; // Amber
  }
  if (
    s === "READY_TO_SHIP" ||
    s === "READY TO SHIP" ||
    s === "READY_TO_PICK" ||
    s === "READY TO PICK"
  ) {
    return { backgroundColor: "#16A34A", color: "#FFFFFF" }; // Green
  }
  if (
    s === "IN_TRANSIT" ||
    s === "IN TRANSIT" ||
    s === "SHIPPED" ||
    s === "OUT_FOR_DELIVERY" ||
    s === "OUT FOR DELIVERY"
  ) {
    return { backgroundColor: "#2563EB", color: "#FFFFFF" }; // Blue with high contrast
  }
  if (s.includes("DELIVERED") || s === "COMPLETE") {
    return { backgroundColor: "#0D9488", color: "#FFFFFF" }; // Teal
  }
  if (s.includes("CANCEL")) {
    return { backgroundColor: "#6B7280", color: "#FFFFFF" }; // Slate Gray
  }
  return { backgroundColor: "#4F46E5", color: "#FFFFFF" }; // Indigo
};

// Map current order status and delivery method to backend status_list key
export const mapOrderStatus = (
  status: string,
  deliveryMethod: string = "DELIVERY"
): string => {
  switch (status.toUpperCase()) {
    case "NEW":
    case "PENDING":
      return "initial_status";
    case "PROCESSING":
    case "PARTIALLY_PROCESSING":
    case "PACKING":
      if (deliveryMethod === "DELIVERY") {
        return "processing_status_shipping";
      } else if (deliveryMethod === "PICKED_UP") {
        return "processing_status_pickup";
      }
      return "processing_status_shipping";
    case "COMPLETE":
    case "DELIVERED":
      return "";
    case "CANCELLED":
      return "";
    case "READY_TO_PICK":
    case "READY TO PICK":
      return "ready_to_picked";
    case "READY_TO_SHIP":
    case "READY TO SHIP":
      return "ready_to_shipped";
    case "OUT_FOR_DELIVERY":
    case "OUT FOR DELIVERY":
      return "out_for_delivery";
    case "SHIPPED":
      return "shipped_status";
    case "RETURN":
    case "REFUNDED":
      return "";
    default:
      return "";
  }
};
